import { createContext, useContext, useId, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

const AccordionCtx = createContext(null);
const AccordionItemCtx = createContext(null);

/**
 * Accordion (ds/disclosure/accordion). type=single|multiple.
 */
export function Accordion({
  type = 'single',
  collapsible = true,
  value: controlledValue,
  defaultValue,
  onValueChange,
  className,
  children,
}) {
  const [uncontrolled, setUncontrolled] = useState(
    defaultValue ?? (type === 'multiple' ? [] : ''),
  );
  const value = controlledValue !== undefined ? controlledValue : uncontrolled;
  const setValue = (next) => {
    if (controlledValue === undefined) setUncontrolled(next);
    onValueChange?.(next);
  };

  const toggle = (itemValue) => {
    if (type === 'multiple') {
      const list = Array.isArray(value) ? value : [];
      const open = list.includes(itemValue);
      setValue(open ? list.filter((v) => v !== itemValue) : [...list, itemValue]);
      return;
    }
    const open = value === itemValue;
    if (open && !collapsible) return;
    setValue(open ? '' : itemValue);
  };

  const isOpen = (itemValue) =>
    type === 'multiple'
      ? Array.isArray(value) && value.includes(itemValue)
      : value === itemValue;

  return (
    <AccordionCtx.Provider value={{ toggle, isOpen }}>
      <div
        data-ds-id="ds/disclosure/accordion"
        data-type={type}
        className={cn('space-y-space-2', className)}
      >
        {children}
      </div>
    </AccordionCtx.Provider>
  );
}

export function AccordionItem({ value, disabled = false, className, children }) {
  const ctx = useContext(AccordionCtx);
  const open = ctx?.isOpen(value);
  const baseId = useId();
  return (
    <div
      data-ds-id={
        disabled
          ? 'ds/disclosure/accordion--disabled'
          : open
            ? 'ds/disclosure/accordion--expanded'
            : 'ds/disclosure/accordion--collapsed'
      }
      data-state={open ? 'open' : 'closed'}
      data-disabled={disabled || undefined}
      className={cn(
        'rounded-ds-lg border border-border bg-card data-[state=open]:border-sage-border',
        disabled && 'pointer-events-none opacity-55',
        className,
      )}
    >
      <AccordionItemCtx.Provider value={{ value, disabled, open, baseId }}>
        {children}
      </AccordionItemCtx.Provider>
    </div>
  );
}

export function AccordionTrigger({ className, children }) {
  const root = useContext(AccordionCtx);
  const item = useContext(AccordionItemCtx);
  if (!root || !item) return null;

  return (
    <button
      type="button"
      id={`${item.baseId}-trigger`}
      aria-expanded={item.open}
      aria-controls={`${item.baseId}-content`}
      disabled={item.disabled}
      onClick={() => root.toggle(item.value)}
      className={cn(
        'flex w-full items-center gap-space-3 px-space-4 py-space-3 text-left text-type-body font-semibold text-foreground',
        className,
      )}
    >
      <span className="min-w-0 flex-1">{children}</span>
      <CaretDown
        className={cn(
          'size-4 shrink-0 text-muted-foreground transition-transform',
          item.open && 'rotate-180',
        )}
        aria-hidden
      />
    </button>
  );
}

export function AccordionContent({ className, children }) {
  const item = useContext(AccordionItemCtx);
  if (!item?.open) return null;
  return (
    <div
      id={`${item.baseId}-content`}
      role="region"
      aria-labelledby={`${item.baseId}-trigger`}
      className={cn(
        'space-y-space-2 overflow-hidden border-t border-border px-space-4 py-space-3 text-type-body-sm text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}
